import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2, Search, BarChart3, RefreshCw, ArrowRight, TrendingUp,
  Globe, Users, Zap, Inbox, MapPin, UserCheck,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { HotLeadsBanner } from "@/components/dashboard/HotLeadsBanner";

const SCORE_COLORS = ["#e11d48", "#f59e0b", "#f59e0b", "#0d9488", "#4e3c97"];

function StatCard({ icon: Icon, label, value, sub, color, testId, delay }) {
  return (
    <Card
      data-testid={testId}
      className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-0.5">
              {label}
            </p>
            <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {value}
            </p>
            {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
          </div>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreBadge({ score, size = "sm" }) {
  const color =
    score >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    score >= 60 ? "bg-teal-50 text-teal-700 border-teal-200" :
    score >= 40 ? "bg-amber-50 text-amber-700 border-amber-200" :
    "bg-rose-50 text-rose-700 border-rose-200";

  return (
    <span className={`inline-flex items-center border rounded-full font-semibold score-badge ${color} ${
      size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm"
    }`}>
      {score}
    </span>
  );
}

export default function DashboardPage() {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error("Dashboard fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/lead-activity?limit=12`, { credentials: "include" });
      if (res.ok) setActivity((await res.json()).activity || []);
    } catch (e) { console.error("Lead activity fetch error:", e); }
  }, [API]);

  useEffect(() => {
    fetchStats();
    fetchActivity();
  }, [fetchStats, fetchActivity]);

  const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const scoreData = stats?.score_distribution || [];
  const statusData = Object.entries(stats?.status_distribution || {}).map(([k, v]) => ({
    name: k, value: v,
  }));
  const PIE_COLORS = ["#4e3c97", "#0d9488", "#f59e0b", "#e11d48"];

  return (
    <div data-testid="dashboard-page" className="space-y-4 animate-fade-in-up">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Prospecting overview at a glance
          </p>
        </div>
        <Button
          data-testid="search-prospects-btn"
          onClick={() => navigate("/prospects")}
          className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <Search className="w-3.5 h-3.5 mr-1.5" />
          Search Prospects
        </Button>
      </div>

      {/* Hot Leads alert */}
      <HotLeadsBanner navigate={navigate} />

      {/* Stats grid - Bento */}
      <div className="bento-grid stagger-children">
        <StatCard
          icon={Building2} label="Prospects" value={stats?.total_companies || 0}
          sub="Saved" color="bg-indigo-50 text-indigo-600"
          testId="stat-total-prospects" delay={0}
        />
        <StatCard
          icon={BarChart3} label="Audits" value={stats?.completed_audits || 0}
          sub={`${stats?.total_audits || 0} total`} color="bg-teal-50 text-teal-600"
          testId="stat-completed-audits" delay={60}
        />
        <StatCard
          icon={Globe} label="Avg Web Score" value={stats?.avg_website_score || 0}
          sub="/100" color="bg-amber-50 text-amber-600"
          testId="stat-avg-website" delay={120}
        />
        <StatCard
          icon={Users} label="CRM Syncs" value={stats?.total_syncs || 0}
          sub="Pushed" color="bg-rose-50 text-rose-600"
          testId="stat-crm-syncs" delay={180}
        />
      </div>

      {/* Charts + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Score Distribution Chart */}
        <Card className="lg:col-span-2 border-slate-200 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreData} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
                  />
                  <Bar dataKey="count" name="Prospects" radius={[4, 4, 0, 0]}>
                    {scoreData.map((_, i) => (
                      <Cell key={i} fill={SCORE_COLORS[i % SCORE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Pie */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Prospect Status
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {statusData.length > 0 ? (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData} cx="50%" cy="50%"
                      innerRadius={50} outerRadius={75} paddingAngle={3}
                      dataKey="value" nameKey="name"
                    >
                      {statusData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 justify-center mt-2">
                  {statusData.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="capitalize text-muted-foreground">{s.name}</span>
                      <span className="font-semibold">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">
                No data yet. Search and save prospects.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Activity Feed */}
      <Card data-testid="lead-activity-feed" className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Inbox className="w-4 h-4 text-indigo-600" /> Lead Activity
          </CardTitle>
          <span className="text-[11px] text-muted-foreground">Inbound form submissions & routing</span>
        </CardHeader>
        <CardContent className="px-0 pb-1">
          {activity.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {activity.map((a) => (
                <button
                  key={a.submission_id}
                  data-testid={`lead-activity-${a.submission_id}`}
                  onClick={() => a.company_id && navigate(`/companies/${a.company_id}`)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                      <Zap className="w-3.5 h-3.5 text-indigo-500" />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-[13px] truncate flex items-center gap-1.5 ${a.read === false ? "font-bold" : "font-medium"}`}>
                        {a.read === false && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />}
                        {a.business_name}
                      </p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                        {a.form_name && <span>{a.form_name}</span>}
                        {(a.city || a.state) && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{[a.city, a.state].filter(Boolean).join(", ")}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.assignment_status === "unassigned" ? (
                      <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Unassigned</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                        <UserCheck className="w-2.5 h-2.5" />{a.owner_name || "Assigned"}
                      </Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground w-14 text-right">{timeAgo(a.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              No lead submissions yet. Share an embed form to start capturing leads.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Prospects */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Prospects
            </CardTitle>
            <Button variant="ghost" size="sm" data-testid="view-all-prospects"
              onClick={() => navigate("/prospects")} className="h-6 text-[11px]">
              View all <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="px-0 pb-1">
            {(stats?.recent_companies || []).length > 0 ? (
              <div className="divide-y divide-slate-100">
                {stats.recent_companies.map((c) => (
                  <button
                    key={c.company_id}
                    data-testid={`recent-company-${c.company_id}`}
                    onClick={() => navigate(`/companies/${c.company_id}`)}
                    className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center shrink-0">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium truncate">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground">{c.category} &middot; {c.city}, {c.state}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.website_score != null && <ScoreBadge score={c.website_score} />}
                      <Badge variant="outline" className="capitalize text-xs">
                        {c.status}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-4 py-4 text-xs text-muted-foreground text-center">
                No prospects saved yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Audits */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Audits
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-1">
            {(stats?.recent_audits || []).length > 0 ? (
              <div className="divide-y divide-slate-100">
                {stats.recent_audits.map((a) => (
                  <button
                    key={a.audit_id}
                    data-testid={`recent-audit-${a.audit_id}`}
                    onClick={() => navigate(`/audit/${a.audit_id}`)}
                    className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium truncate">{a.company_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {a.types?.join(", ")} audit
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`text-xs ${
                          a.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : a.status === "running"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}
                        variant="outline"
                      >
                        {a.status}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-4 py-4 text-xs text-muted-foreground text-center">
                No audits run yet
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
